<template>
  <modal
    elem-id="modal_anticipation_detail"
    size="compactDesktop"
    :title="modalTitle"
    :close-label="t('Fechar')"
    :save-label="''"
    :show="isVisible"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <!-- Content only renders when modal is visible (prevents API calls on mount) -->
    <template v-if="isVisible">
      <!-- Loading state -->
      <div v-if="isLoading" class="loading-container">
        <i class="pi pi-spin pi-spinner" style="font-size: 2rem;"></i>
        <p>{{ t('Carregando...') }}</p>
      </div>

      <!-- View mode: Show anticipation details -->
      <div v-else-if="anticipation">
        <!-- Status badge -->
        <div class="status-section">
          <AnticipationStatusBadge
            :status="anticipation.status"
            :anticipation="anticipation"
            :show-details="true"
          />
        </div>

        <!-- Main details -->
        <box
          type="primary"
          :title="t('Detalhes da Antecipacao')"
          elem-id="boxDetailsAnticipation">

          <CompactDataTable :value="detailsData as any" />
        </box>

        <!-- Financial breakdown -->
        <box
          type="primary"
          :title="t('Resumo Financeiro')"
          elem-id="boxFinancialAnticipation">

          <div class="financial-grid">
            <div class="financial-item">
              <span class="financial-label">{{ t('Valor Total') }}</span>
              <span class="financial-value">{{ formatCurrency((anticipation.total_value ?? 0) / 100) }}</span>
            </div>
            <div class="financial-item">
              <span class="financial-label">{{ t('Taxa de Antecipacao') }}</span>
              <span class="financial-value fee">- {{ formatCurrency((anticipation.fee ?? 0) / 100) }}</span>
            </div>
            <Divider />
            <div class="financial-item highlight">
              <span class="financial-label">{{ t('Valor Liquido') }}</span>
              <span class="financial-value success">{{ formatCurrency((anticipation.net_value ?? 0) / 100) }}</span>
            </div>
          </div>
        </box>

        <!-- Linked Recebimentos -->
        <box
          v-if="linkedRecebimentos && linkedRecebimentos.length > 0"
          type="primary"
          :title="t('Recebiveis Vinculados')"
          elem-id="boxRecebimentosAnticipation">

          <div class="recebimentos-list">
            <div
              v-for="rec in linkedRecebimentos"
              :key="rec.id"
              class="recebimento-item"
            >
              <div class="recebimento-info">
                <span class="recebimento-value">{{ formatCurrency((rec.valor ?? 0) / 100) }}</span>
                <span class="recebimento-date">{{ formatDate(rec.data_vencimento) }}</span>
                <Tag
                  v-if="rec.status"
                  :value="rec.status"
                  :severity="getRecebimentoStatusSeverity(rec.status)"
                  class="recebimento-status"
                />
              </div>
              <div v-if="rec.cliente_nome" class="recebimento-cliente">
                {{ rec.cliente_nome }}
              </div>
            </div>
          </div>
        </box>

        <!-- Provider info -->
        <box
          v-if="anticipation.provider_anticipation_id"
          type="secondary"
          :title="t('Informações do Provedor')"
          elem-id="boxProviderAnticipation">

          <CompactDataTable :value="providerData as any" />
        </box>

        <!-- Action buttons -->
        <div class="actions-section">
          <!-- Sync button - shows when not in final status -->
          <Button
            v-if="canSync"
            data-testid="anticipation-detail-sync-button"
            :label="t('Sincronizar')"
            icon="pi pi-sync"
            severity="secondary"
            outlined
            class="action-button"
            :loading="isSyncing"
            :disabled="isSyncing"
            v-tooltip.top="t('Buscar status atualizado do provedor')"
            @click="handleSync"
          />

          <!-- Cancel button -->
          <Button
            v-if="canCancelAnticipation"
            data-testid="anticipation-detail-cancel-button"
            :label="t('Cancelar Antecipacao')"
            icon="pi pi-times"
            severity="danger"
            outlined
            class="action-button"
            @click="handleCancel"
          />
        </div>
      </div>

      <!-- Error state -->
      <div v-else class="error-container">
        <i class="pi pi-exclamation-circle" style="font-size: 2rem; color: var(--red-500);"></i>
        <p>{{ t('Antecipacao nao encontrada') }}</p>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log } from '../../utils/DebugLogger';
import { useAnticipations } from '../../composables/useAnticipations';
import { useConfirmAction } from '../../composables/useConfirmAction';
import { useDateFormatter } from '../../composables/useDateFormatter';
import AnticipationStatusBadge from './AnticipationStatusBadge.vue';
import CompactDataTable from '../../grid/CompactDataTable.vue';

type Id = number | string;

interface Recebimento {
  id?: Id;
  valor?: number;
  data_vencimento?: string;
  status?: string;
  cliente_nome?: string;
}

interface Anticipation {
  id?: Id;
  status?: string;
  total_value?: number;
  fee?: number;
  net_value?: number;
  anticipation_days?: number;
  request_date?: string;
  anticipation_date?: string;
  due_date?: string;
  created_at?: string;
  updated_at?: string;
  provider_anticipation_id?: string;
  provider?: string;
  provider_status?: string;
  recebimentos?: Recebimento[];
  [key: string]: unknown;
}

interface SyncResult {
  status?: string;
  old_status?: string;
  new_status?: string;
  message?: string;
}

interface DetailRow {
  prop: string;
  value: unknown;
}

interface ModalOpenPayload {
  id?: Id;
}

const { t } = useI18n();
const { confirmAction } = useConfirmAction();
const { formatDate: formatDateLocale, formatDateTime: formatDateTimeLocale } = useDateFormatter();
const {
  getById,
  cancel,
  syncAnticipation,
  getStatusConfig,
  isFinalStatus,
  canCancel: checkCanCancel
} = useAnticipations();

// State
const isVisible = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const isSyncing = ref<boolean>(false);
const anticipation = ref<Anticipation | null>(null);
const linkedRecebimentos = ref<Recebimento[]>([]);

// Computed
const modalTitle = computed<string>(() => {
  if (anticipation.value) {
    return `${t('Antecipacao')} #${anticipation.value.id}`;
  }
  return t('Detalhes da Antecipacao');
});

const canCancelAnticipation = computed<boolean>(() => {
  return !!anticipation.value && checkCanCancel(anticipation.value);
});

const canSync = computed<boolean>(() => {
  if (!anticipation.value) return false;
  return !isFinalStatus(anticipation.value.status);
});

const detailsData = computed<DetailRow[]>(() => {
  if (!anticipation.value) return [];

  const ant = anticipation.value;
  const statusConfig = getStatusConfig(ant.status) as { label: string };
  const data: DetailRow[] = [
    { prop: 'ID', value: ant.id },
    { prop: t('Status'), value: t(statusConfig.label) },
    { prop: t('Dias Antecipados'), value: ant.anticipation_days || '-' }
  ];

  if (ant.request_date) {
    data.push({ prop: t('Data Solicitacao'), value: formatDate(ant.request_date) });
  }

  if (ant.anticipation_date) {
    data.push({ prop: t('Data do Credito'), value: formatDate(ant.anticipation_date) });
  }

  if (ant.due_date) {
    data.push({ prop: t('Vencimento Original'), value: formatDate(ant.due_date) });
  }

  data.push({ prop: t('Criado em'), value: formatDateTime(ant.created_at) });

  if (ant.updated_at) {
    data.push({ prop: t('Atualizado em'), value: formatDateTime(ant.updated_at) });
  }

  return data;
});

const providerData = computed<DetailRow[]>(() => {
  if (!anticipation.value) return [];

  const ant = anticipation.value;
  const data: DetailRow[] = [];

  if (ant.provider_anticipation_id) {
    data.push({ prop: t('ID do Provedor'), value: ant.provider_anticipation_id });
  }

  if (ant.provider) {
    data.push({ prop: t('Provedor'), value: ant.provider });
  }

  if (ant.provider_status) {
    data.push({ prop: t('Status no Provedor'), value: ant.provider_status });
  }

  return data;
});

// Methods
const formatCurrency = (value?: number | null): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0);
};

const formatDate = (dateStr?: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return formatDateLocale(date);
};

const formatDateTime = (dateStr?: string | null): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return formatDateTimeLocale(date);
};

const getRecebimentoStatusSeverity = (status?: string): string => {
  const severityMap: Record<string, string> = {
    'pago': 'success',
    'pendente': 'warning',
    'atrasado': 'danger',
    'cancelado': 'secondary'
  };
  return severityMap[status?.toLowerCase() || ''] || 'info';
};

const loadAnticipation = async (id: Id): Promise<void> => {
  log('AnticipationDetailModal', 'loadAnticipation', 'Loading anticipation', id);
  isLoading.value = true;

  try {
    anticipation.value = await getById(Number(id)) as unknown as Anticipation;
    log('AnticipationDetailModal', 'loadAnticipation', 'Loaded', anticipation.value);

    // Load linked recebimentos if available
    if (anticipation.value?.recebimentos) {
      linkedRecebimentos.value = anticipation.value.recebimentos;
    } else {
      linkedRecebimentos.value = [];
    }
  } catch (error) {
    log('AnticipationDetailModal', 'loadAnticipation', 'Error', error);
    eventBus.emit('globalMessage.error', t('Erro ao carregar antecipacao'));
    anticipation.value = null;
  } finally {
    isLoading.value = false;
  }
};

const handleSync = async (): Promise<void> => {
  if (!anticipation.value?.id) return;

  log('AnticipationDetailModal', 'handleSync', 'Syncing anticipation', anticipation.value.id);
  isSyncing.value = true;

  try {
    const result = await syncAnticipation(Number(anticipation.value.id)) as { sync_result?: SyncResult };
    log('AnticipationDetailModal', 'handleSync', 'Sync result', result);

    const syncResult: SyncResult = result.sync_result || {};
    if (syncResult.status === 'synced') {
      eventBus.emit('globalMessage.success',
        t('Antecipacao sincronizada') + `: ${syncResult.old_status} → ${syncResult.new_status}`
      );
    } else if (syncResult.status === 'unchanged') {
      eventBus.emit('globalMessage.info', t('Status nao alterado'));
    } else {
      eventBus.emit('globalMessage.warning', syncResult.message || t('Sincronizacao concluida'));
    }

    eventBus.emit('anticipation.synced', { anticipationId: anticipation.value.id });

    // Reload the anticipation to show updated status
    await loadAnticipation(anticipation.value.id);
  } catch (error) {
    log('AnticipationDetailModal', 'handleSync', 'Error', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', message || t('Erro ao sincronizar antecipacao'));
  } finally {
    isSyncing.value = false;
  }
};

const performCancel = async (): Promise<void> => {
  if (!anticipation.value?.id) return;

  log('AnticipationDetailModal', 'performCancel', 'Cancelling anticipation', anticipation.value.id);
  isLoading.value = true;

  try {
    await cancel(Number(anticipation.value.id));
    eventBus.emit('globalMessage.success', t('Antecipacao cancelada com sucesso'));
    eventBus.emit('anticipation.cancelled', { anticipationId: anticipation.value.id });

    // Reload the anticipation to show updated status
    await loadAnticipation(anticipation.value.id);
  } catch (error) {
    log('AnticipationDetailModal', 'performCancel', 'Error', error);
    const message = error instanceof Error ? error.message : String(error);
    eventBus.emit('globalMessage.error', message || t('Erro ao cancelar antecipacao'));
  } finally {
    isLoading.value = false;
  }
};

const handleCancel = (): void => {
  if (!anticipation.value?.id) return;

  confirmAction({
    message: t('Tem certeza que deseja cancelar esta antecipacao?'),
    header: t('Confirmação'),
    icon: 'pi pi-question-circle',
    acceptLabel: t('Sim'),
    rejectLabel: t('Não'),
    onAccept: () => performCancel()
  });
};

const handleClose = (): void => {
  log('AnticipationDetailModal', 'handleClose', 'Closing modal');
  isVisible.value = false;
  anticipation.value = null;
  linkedRecebimentos.value = [];
};

// Event handlers
const handleModalOpen = (data?: ModalOpenPayload): void => {
  log('AnticipationDetailModal', 'handleModalOpen', 'Received open event', data);

  if (data?.id) {
    loadAnticipation(data.id);
    isVisible.value = true;
  }
};

// Lifecycle
onMounted(() => {
  log('AnticipationDetailModal', 'onMounted', 'Component mounted');

  // Standard modal events
  eventBus.on('modal.anticipation.open', handleModalOpen);
  eventBus.on('modal.anticipation.close', handleClose);

  // Legacy events (for backward compatibility)
  eventBus.on('anticipation.view', handleModalOpen);
});

onBeforeUnmount(() => {
  log('AnticipationDetailModal', 'onBeforeUnmount', 'Cleaning up event listeners');

  eventBus.off('modal.anticipation.open', handleModalOpen);
  eventBus.off('modal.anticipation.close', handleClose);
  eventBus.off('anticipation.view', handleModalOpen);
});

// Expose methods
defineExpose({
  open: handleModalOpen,
  close: handleClose
});
</script>

<style scoped>
.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;
  color: var(--text-color-secondary);
}

.status-section {
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.financial-grid {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.financial-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.financial-item.highlight {
  padding: 0.75rem;
  background: var(--surface-ground);
  border-radius: 6px;
  margin-top: 0.5rem;
}

.financial-label {
  color: var(--text-color-secondary);
  font-size: 0.875rem;
}

.financial-value {
  font-weight: 600;
  font-size: 1rem;
}

.financial-value.fee {
  color: var(--red-500);
}

.financial-value.success {
  color: var(--green-600);
  font-size: 1.125rem;
}

.recebimentos-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.recebimento-item {
  padding: 0.75rem;
  background-color: var(--surface-ground);
  border-radius: 4px;
  border: 1px solid var(--surface-border);
}

.recebimento-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.recebimento-value {
  font-weight: 600;
  color: var(--text-color);
}

.recebimento-date {
  color: var(--text-color-secondary);
  font-size: 0.875rem;
}

.recebimento-status {
  font-size: 0.7rem;
}

.recebimento-cliente {
  margin-top: 0.25rem;
  font-size: 0.8rem;
  color: var(--text-color-secondary);
}

.actions-section {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--surface-border);
}

.action-button {
  width: 100%;
}
</style>
